'use client';
import React, { useState } from 'react';
import AnnouncementBar from '@/components/AnnouncementBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface FormState {
  name: string;
  email: string;
  message: string;
}

export default function EnquiriesPage() {
  const [form, setForm] = useState<FormState>({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<FormState>>({});

  const validate = (): boolean => {
    const newErrors: Partial<FormState> = {};
    if (!form.name.trim()) newErrors.name = 'Name is required.';
    if (!form.email.trim()) newErrors.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Please enter a valid email address.';
    if (!form.message.trim()) newErrors.message = 'Please include a brief message.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setSubmitted(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormState]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const sectionLabelStyle: React.CSSProperties = {
    fontFamily: 'adobe-garamond-pro, Georgia, serif',
    fontSize: '11px',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#8A9E8C',
    marginBottom: '16px',
  };

  const sectionHeadingStyle: React.CSSProperties = {
    fontFamily: 'orpheus-pro, Georgia, serif',
    fontSize: 'clamp(22px, 2.5vw, 34px)',
    fontWeight: '400',
    color: '#1A2332',
    lineHeight: '1.2',
    marginBottom: '24px',
  };

  const dividerBarStyle: React.CSSProperties = {
    width: '40px',
    height: '1px',
    backgroundColor: '#8A9E8C',
    marginBottom: '28px',
  };

  const bodyTextStyle: React.CSSProperties = {
    fontFamily: 'adobe-garamond-pro, Georgia, serif',
    fontSize: 'clamp(16px, 1.3vw, 19px)',
    color: '#4A4A4A',
    lineHeight: '1.85',
    marginBottom: '20px',
  };

  const feeTagStyle: React.CSSProperties = {
    display: 'inline-block',
    fontFamily: 'adobe-garamond-pro, Georgia, serif',
    fontSize: 'clamp(15px, 1.2vw, 17px)',
    color: '#1A2332',
    backgroundColor: 'rgba(138,158,140,0.15)',
    border: '1px solid rgba(138,158,140,0.4)',
    padding: '8px 18px',
    marginTop: '8px',
    letterSpacing: '0.04em',
  };

  const sectionDivider = (
    <div style={{ borderTop: '1px solid rgba(26,35,50,0.1)', marginBottom: 'clamp(48px, 6vw, 80px)' }} />
  );

  return (
    <div style={{ backgroundColor: '#EFE3E1', minHeight: '100vh' }}>
      <AnnouncementBar />
      <Header />

      <main>
        {/* ── Hero — Oxford Door Image ── */}
        <section
          className="w-full"
          style={{
            position: 'relative',
            backgroundImage: 'url(/assets/images/6213a810-69da-445d-a2a4-7e2ee270c1b0-1782134989081.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            paddingTop: 'clamp(72px, 9vw, 128px)',
            paddingBottom: 'clamp(72px, 9vw, 128px)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(26,35,50,0.62)',
              zIndex: 0,
            }}
          />
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <p style={sectionLabelStyle}>
              Get in Touch
            </p>
            <h1
              style={{
                fontFamily: 'orpheus-pro, Georgia, serif',
                fontSize: 'clamp(36px, 5vw, 72px)',
                fontWeight: '400',
                color: '#F5F0EA',
                lineHeight: '1.1',
                maxWidth: '600px',
                marginBottom: '24px',
              }}
            >
              Appointments &amp;<br />
              <em style={{ fontStyle: 'normal' }}>Fees</em>
            </h1>
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: 'clamp(15px, 1.3vw, 19px)',
                color: 'rgba(239,227,225,0.65)',
                lineHeight: '1.7',
                maxWidth: '520px',
              }}
            >
              All enquiries are handled with complete confidentiality and responded to promptly.
            </p>
          </div>
        </section>

        {/* ── Main Content ── */}
        <section
          style={{
            backgroundColor: '#EFE3E1',
            paddingTop: 'clamp(72px, 9vw, 120px)',
            paddingBottom: 'clamp(72px, 9vw, 120px)',
          }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">

              {/* Left column — content sections */}
              <div style={{ flex: '1 1 0', minWidth: 0 }}>

                {/* Intro */}
                <div style={{ marginBottom: 'clamp(48px, 6vw, 80px)' }}>
                  <p style={{ ...bodyTextStyle, marginBottom: 0 }}>
                    Taking the first step towards therapy is an investment in your emotional wellbeing, relationships, and long-term psychological health.
                  </p>
                  <p style={{ ...bodyTextStyle, marginTop: '20px', marginBottom: 0 }}>
                    If you are considering therapy and would like to explore whether my approach may be right for you, you are welcome to complete the enquiry form or contact me by email. I will be happy to answer any questions you may have and, where appropriate, arrange an initial consultation.
                  </p>
                </div>

                {sectionDivider}

                {/* Initial Consultation */}
                <div style={{ marginBottom: 'clamp(48px, 6vw, 80px)' }}>
                  <p style={sectionLabelStyle}>Initial Consultation</p>
                  <h2 style={sectionHeadingStyle}>Initial Consultation</h2>
                  <div style={dividerBarStyle} />
                  <p style={bodyTextStyle}>
                    The initial consultation lasts approximately 90 minutes and provides an opportunity to explore your current concerns, personal history, and goals for therapy. Together, we will develop a shared understanding of your difficulties and consider the most appropriate therapeutic approach.
                  </p>
                  <span style={feeTagStyle}>Fee: £250</span>
                </div>

                {sectionDivider}

                {/* Ongoing Therapy */}
                <div style={{ marginBottom: 'clamp(48px, 6vw, 80px)' }}>
                  <p style={sectionLabelStyle}>Ongoing Therapy</p>
                  <h2 style={sectionHeadingStyle}>Ongoing Therapy</h2>
                  <div style={dividerBarStyle} />
                  <p style={bodyTextStyle}>
                    Therapy sessions are 50 minutes in duration and are individually tailored to support meaningful and lasting psychological change.
                  </p>
                  <span style={feeTagStyle}>Fee: £180 per session</span>
                </div>

                {sectionDivider}

                {/* In-Person and Online */}
                <div style={{ marginBottom: 'clamp(48px, 6vw, 80px)' }}>
                  <p style={sectionLabelStyle}>Appointments</p>
                  <h2 style={sectionHeadingStyle}>In-Person and Online Consultations</h2>
                  <div style={dividerBarStyle} />
                  <p style={bodyTextStyle}>
                    Appointments are available in person in Wales and remotely via secure Zoom video consultation. I also work with clients throughout the UK and internationally online. Many people choose to combine in-person and online sessions, allowing therapy to remain consistent around professional commitments, family life, or travel.
                  </p>
                  <p style={{ ...bodyTextStyle, marginBottom: 0 }}>
                    Whichever format you choose, the focus remains on providing a confidential, attentive, and personalised therapeutic experience.
                  </p>
                </div>

                {sectionDivider}

                {/* Professional Reports */}
                <div style={{ marginBottom: 'clamp(48px, 6vw, 80px)' }}>
                  <p style={sectionLabelStyle}>Professional Services</p>
                  <h2 style={sectionHeadingStyle}>Professional Reports and Liaison</h2>
                  <div style={dividerBarStyle} />
                  <p style={bodyTextStyle}>
                    Where appropriate, I provide comprehensive psychological reports, professional letters, and liaison with other healthcare professionals to support a shared understanding of needs and ensure continuity of care.
                  </p>
                  <p style={{ ...bodyTextStyle, marginBottom: 0 }}>
                    Fees for this work are based on the time and complexity involved and will always be discussed in advance.
                  </p>
                </div>

                {sectionDivider}

                {/* Payment and Cancellation */}
                <div>
                  <p style={sectionLabelStyle}>Policy</p>
                  <h2 style={sectionHeadingStyle}>Payment and Cancellation Policy</h2>
                  <div style={dividerBarStyle} />
                  <p style={bodyTextStyle}>
                    Payment is requested on the day of your appointment by bank transfer or card.
                  </p>
                  <p style={bodyTextStyle}>
                    To ensure that appointment times can be reserved exclusively for each client, cancellations or changes made with less than 48 hours&apos; notice will normally be charged at the full session fee. I appreciate that unexpected circumstances can arise, and any exceptional situations will be considered with understanding.
                  </p>
                  <p style={{ ...bodyTextStyle, marginBottom: 0 }}>
                    If you have any questions about appointments, fees, or whether therapy may be right for you, please do not hesitate to get in touch.
                  </p>
                </div>

              </div>

              {/* Right column — contact form */}
              <div style={{ flex: '0 0 auto', width: '100%', maxWidth: '480px' }}>
                <div
                  style={{
                    backgroundColor: '#F5F0EA',
                    padding: 'clamp(32px, 4vw, 52px)',
                    position: 'sticky',
                    top: '120px',
                  }}
                >
                  <p
                    style={{
                      fontFamily: 'adobe-garamond-pro, Georgia, serif',
                      fontSize: '11px',
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: '#8A9E8C',
                      marginBottom: '16px',
                    }}
                  >
                    Contact
                  </p>
                  <h3
                    style={{
                      fontFamily: 'orpheus-pro, Georgia, serif',
                      fontSize: 'clamp(22px, 2.2vw, 30px)',
                      fontWeight: '400',
                      color: '#1A2332',
                      lineHeight: '1.25',
                      marginBottom: '8px',
                    }}
                  >
                    Send an Enquiry
                  </h3>
                  <p
                    style={{
                      fontFamily: 'adobe-garamond-pro, Georgia, serif',
                      fontSize: '14px',
                      color: '#6A6A6A',
                      lineHeight: '1.6',
                      marginBottom: '36px',
                    }}
                  >
                    All messages are treated in strict confidence.
                  </p>

                  {/* ⚠️ PLACEHOLDER NOTE — Email not yet connected */}
                  <div
                    style={{
                      backgroundColor: 'rgba(138,158,140,0.15)',
                      border: '1px solid rgba(138,158,140,0.4)',
                      borderLeft: '3px solid #8A9E8C',
                      padding: '12px 16px',
                      marginBottom: '28px',
                    }}
                  >
                    <p
                      style={{
                        fontFamily: 'adobe-garamond-pro, Georgia, serif',
                        fontSize: '13px',
                        color: '#4A4A4A',
                        lineHeight: '1.6',
                      }}
                    >
                      <strong style={{ color: '#1A2332' }}>⚠ Placeholder — Email not yet connected.</strong>{' '}
                      Dr Morgan&apos;s email address needs to be confirmed and an email service integrated before this form will deliver submissions.
                    </p>
                  </div>

                  {submitted ? (
                    <div style={{ padding: '32px 0', textAlign: 'center' }}>
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#8A9E8C',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 20px',
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <path d="M4 10l4.5 4.5L16 6" stroke="#F5F0EA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <p style={{ fontFamily: 'orpheus-pro, Georgia, serif', fontSize: '20px', color: '#1A2332', marginBottom: '12px' }}>
                        Thank you
                      </p>
                      <p style={{ fontFamily: 'adobe-garamond-pro, Georgia, serif', fontSize: '15px', color: '#4A4A4A', lineHeight: '1.7' }}>
                        Your message has been received. Dr Morgan will be in touch shortly.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} noValidate>
                      <style>{`
                        .enquiry-input {
                          width: 100%;
                          background: transparent;
                          border: none;
                          border-bottom: 1px solid rgba(26,35,50,0.2);
                          padding: 12px 0;
                          font-family: adobe-garamond-pro, Georgia, serif;
                          font-size: 16px;
                          color: #1A2332;
                          outline: none;
                          transition: border-color 0.2s ease;
                          box-sizing: border-box;
                        }
                        .enquiry-input::placeholder { color: rgba(74,74,74,0.45); }
                        .enquiry-input:focus { border-bottom-color: #1A2332; }
                        .enquiry-input.error { border-bottom-color: #B85C5C; }
                        .enquiry-textarea { resize: vertical; min-height: 120px; }
                        .enquiry-error {
                          font-family: adobe-garamond-pro, Georgia, serif;
                          font-size: 12px;
                          color: #B85C5C;
                          margin-top: 6px;
                          display: block;
                        }
                        .enquiry-submit {
                          width: 100%;
                          background-color: #1A2332;
                          color: #EFE3E1;
                          border: none;
                          padding: 16px 24px;
                          font-family: adobe-garamond-pro, Georgia, serif;
                          font-size: 13px;
                          letter-spacing: 0.12em;
                          text-transform: uppercase;
                          cursor: pointer;
                          transition: background-color 0.25s ease;
                          margin-top: 8px;
                        }
                        .enquiry-submit:hover { background-color: #2C2C2C; }
                      `}</style>

                      <div style={{ marginBottom: '28px' }}>
                        <label htmlFor="name" style={{ fontFamily: 'adobe-garamond-pro, Georgia, serif', fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8A9E8C', display: 'block', marginBottom: '4px' }}>
                          Name
                        </label>
                        <input id="name" name="name" type="text" value={form.name} onChange={handleChange} placeholder="Your full name" className={`enquiry-input${errors.name ? ' error' : ''}`} required />
                        {errors.name && <span className="enquiry-error">{errors.name}</span>}
                      </div>

                      <div style={{ marginBottom: '28px' }}>
                        <label htmlFor="email" style={{ fontFamily: 'adobe-garamond-pro, Georgia, serif', fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8A9E8C', display: 'block', marginBottom: '4px' }}>
                          Email
                        </label>
                        <input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="your@email.com" className={`enquiry-input${errors.email ? ' error' : ''}`} required />
                        {errors.email && <span className="enquiry-error">{errors.email}</span>}
                      </div>

                      <div style={{ marginBottom: '36px' }}>
                        <label htmlFor="message" style={{ fontFamily: 'adobe-garamond-pro, Georgia, serif', fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8A9E8C', display: 'block', marginBottom: '4px' }}>
                          Nature of Enquiry
                        </label>
                        <textarea id="message" name="message" value={form.message} onChange={handleChange} placeholder="Please briefly describe the nature of your enquiry…" className={`enquiry-input enquiry-textarea${errors.message ? ' error' : ''}`} required />
                        {errors.message && <span className="enquiry-error">{errors.message}</span>}
                      </div>

                      <button type="submit" className="enquiry-submit">
                        Submit Enquiry
                      </button>
                    </form>
                  )}
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
